import React, { useEffect } from 'react';
import * as elog from '../elog';
import gql from 'graphql-tag';
import SignupSigninModal from '@reverbdotcom/commons/src/components/signup_signin_modal';
import { RCAlertBox } from '@reverbdotcom/commons/src/rc/RCAlertBox';
import I18n from 'i18n-js';
import { flowRight, includes } from 'lodash';
import { MutationFn } from 'react-apollo';
import {
  CreateMyWatches,
  DeleteMyWatches,
  core_apimessages_Channel,
  WatchBadgeData,
} from '../rql';
import classNames from 'classnames';
import { MParticleEventName, ProductActionType, trackEvent } from '../elog/mparticle_tracker';
import { getWatchNudgeText } from '../nudge_manager';
import { RCIcon } from '../rc/RCIcon';
import HeartEmptySvg from '../../design-system/icons/core/heart-empty.svg?component';
import HeartFilledSvg from '../../design-system/icons/core/heart-filled.svg?component';
import { withGraphql } from '../with_graphql';
import { WatchUnwatchAnimation } from './animations/animations';
import { WatchParentComponentName } from '../event_tracking/watchlist';
import { RCTooltip } from '../rc/RCTooltip';
import { useUser } from '../user_hooks';

const COMPONENT_NAME = 'watch_badge';
const WATCH_KEY = 'watch_id';
export enum DisplayStyle {
  ICON,
  BUTTON,
  TEXT,
  ANIMATION_ICON,
  ANIMATION_BUTTON,
}

export const WatchBadgeFragment = gql`
  fragment WatchBadgeData on Listing {
    _id
    id
    title
    watching
    watchThumbnails: images(input: { type: "Product", scope: "photos", transform: "card_square", count: 1}) {
      source
    }
  }
`;

/*
this data only exists if a parent component situationally queries it (ex. bump rows).
watch badge will not hydrate this data on its own.
if it did, all watches for listings that happen to be bumped
would count as bump clicks (even if they appeared organically!).
*/
interface BumpKeyData { bumpKey?: any }

interface IProps {
  listing: WatchBadgeData.Fragment & BumpKeyData;
  watchSource?: core_apimessages_Channel;
  createWatch?: MutationFn<CreateMyWatches.Mutation, CreateMyWatches.Variables>;
  deleteWatch?: MutationFn<DeleteMyWatches.Mutation, DeleteMyWatches.Variables>;
  displayStyle: DisplayStyle;
  enableNudge?: boolean;
  position?: number;
  // please pass a WatchParentComponentName value when possible instead of a plain string!
  parentComponentName: WatchParentComponentName | string;
}

const loggedOut = (user) => user?.loggedOut ?? true;
const ownsListing = (listing, user) => !loggedOut(user) && user.id === parseInt(listing.sellerId);
const isIcon = (displayStyle) => includes([DisplayStyle.ICON, DisplayStyle.ANIMATION_ICON], displayStyle);
const showDisabledWithTooltip = (listing, user, displayStyle) => ownsListing(listing, user) && !isIcon(displayStyle);

function WatchIconAndText(props) {
  const { displayStyle, watching } = props;
  const animate = includes(
    [DisplayStyle.ANIMATION_BUTTON, DisplayStyle.ANIMATION_ICON],
    displayStyle,
  );

  return (
    <div className="watch-badge__inner">
      <div className="watch-badge__icon watch-badge__icon--heart">
        {animate
          ? <div className='watch-badge__animation'><WatchUnwatchAnimation watching={watching} /></div>
          : <RCIcon svgComponent={watching ? HeartFilledSvg : HeartEmptySvg} data-watch-heart />
        }
      </div>
      {!isIcon(displayStyle) && (
        <span className="watch-badge__text">
          {watching ?
            I18n.t('commons.watchBadge.unwatch') :
            I18n.t('commons.watchBadge.watch')
          }
        </span>
      )}
    </div>
  );
}

function DisabledWithTooltip(props) {
  const user = useUser();
  const { listing, displayStyle, watching, classes } = props;

  if (!showDisabledWithTooltip(listing, user, displayStyle)) return null;

  return (
    <div role="button" aria-disabled={true} className={classes}>
      <RCTooltip
        text={I18n.t('commons.watchBadge.own_listing_tooltip')}
        placement="bottom"
        type="plain"
      >
        <div className="watch-badge__button">
          <WatchIconAndText
            displayStyle={displayStyle}
            watching={watching}
          />
        </div>
      </RCTooltip>
    </div>
  );
}

export function InternalWatchBadge(props: IProps) {
  const user = useUser();

  useEffect(() => {
    if (!loggedOut(user)) {
      const { listing } = props;
      const watchId = localStorage.getItem(WATCH_KEY);
      if (watchId === listing.id) {
        localStorage.removeItem(WATCH_KEY);
        if (!listing.watching && !ownsListing(listing, user)) {
          handleWatch(props);
        }
      }
    }
  });

  const { listing, displayStyle = DisplayStyle.BUTTON, enableNudge } = props;
  const watching = listing.watching;
  const nudgeText = getWatchNudgeText(user, listing);

  const [isSignupSigninModalOpen, setIsSignupSigninModalOpen] = React.useState(false);
  const watchThumbnails = listing?.watchThumbnails || [];
  const thumbnail = watchThumbnails[0]?.source || '';

  const classes = classNames(
    'watch-badge',
    { 'watch-badge--watching': listing.watching },
    { 'watch-badge--button': displayStyle === DisplayStyle.BUTTON },
    { 'watch-badge--text': displayStyle === DisplayStyle.TEXT },
    { 'watch-badge--icon-only': displayStyle === DisplayStyle.ICON },
    { 'watch-badge--animation-icon': displayStyle === DisplayStyle.ANIMATION_ICON },
    { 'watch-badge--animation-button': displayStyle === DisplayStyle.ANIMATION_BUTTON },
    { 'watch-badge--disabled': ownsListing(listing, user) },
  );

  if (ownsListing(listing, user)) {
    return (
      <DisabledWithTooltip
        listing={listing}
        displayStyle={displayStyle}
        watching={watching}
        classes={classes}
      />
    );
  }

  return (
    <>
      <div className={classes}>
        <button
          className="watch-badge__button"
          onClick={(e) => handleClick(e, props, loggedOut(user), () => setIsSignupSigninModalOpen(true))}
          type="button"
          title={watching ?
            I18n.t('commons.watchBadge.unwatch_tooltip') :
            I18n.t('commons.watchBadge.watch_tooltip')
          }
        >
          <WatchIconAndText
            displayStyle={displayStyle}
            watching={watching}
          />
        </button>
        {enableNudge && nudgeText && (
          <div className="watch-badge__nudge">
            {nudgeText}
          </div>
        )}
      </div>
      <SignupSigninModal
        onRequestClose={() => setIsSignupSigninModalOpen(false)}
        active={isSignupSigninModalOpen}
        messageComponent={
          <WatchBenefits
            thumbnail={thumbnail}
            alt={listing.title}
          />
        }
        trackSource="WatchBadge"
        onlyShowMessageOnTab='signup'
      />
    </>
  );
}

function WatchBenefits(props: { thumbnail: string, alt: string })  {
  return (
    <div
      id="watch-benefits"
      className="scaling-mb-2"
    >
      <RCAlertBox type="info">
        <h4 className="align-center size-110 weight-bold mb-1">
          {I18n.t('commons.watchBadge.benefits.signUp')}
        </h4>
        <div className="d-flex fx-align-center">
          <div className="fx-width-20 mr-1">
            <img src={props.thumbnail} alt={props.alt} role="presentation" className="d-block width-100" />
          </div>
          <ul className="size-80">
            <li className="d-flex">
              <span className="icon-l-check color-green" />
              <span>
                {I18n.t('commons.watchBadge.benefits.priceDrop')}
              </span>
            </li>
            <li className="d-flex mt-half">
              <span className="icon-l-check color-green" />
              {I18n.t('commons.watchBadge.benefits.offers')}
            </li>
            <li className="d-flex mt-half">
              <span className="icon-l-check color-green" />
              {I18n.t('commons.watchBadge.benefits.save')}
            </li>
          </ul>
        </div>
      </RCAlertBox>
    </div>
  );
}

const createWatch =
  withGraphql<IProps, CreateMyWatches.Mutation, CreateMyWatches.Variables>(
    gql`
      mutation CreateMyWatches($input: Input_core_apimessages_WatchesCreateRequest) {
        createWatch(input: $input) {
          id
          listing {
            id
            _id
            watching
          }
        }
      }
    `,
    {
      name: 'createWatch',
      options: (ownProps) => {
        const { listing, watchSource } = ownProps;
        return {
          variables: {
            input: {
              listingId: listing.id,
              channel: watchSource,
              bumpKey: listing.bumpKey?.key ?
                { key: listing.bumpKey.key } :
                null,
            },
          },
          optimisticResponse: {
            __typename: 'Mutation',
            createWatch: {
              __typename: 'core_apimessages_UpdateWatchResponse',
              id: -1,
              listing: {
                id: listing.id,
                _id: listing._id,
                watching: true,
                __typename: 'Listing',
              },
            },
          },
        };
      },
    },
  );

const deleteWatch =
  withGraphql<IProps, DeleteMyWatches.Mutation, DeleteMyWatches.Variables>(
    gql`
      mutation DeleteMyWatches($input: Input_core_apimessages_WatchesDeleteRequest) {
        deleteWatch(input: $input) {
          id
          listing {
            _id
            id
            watching
          }
        }
      }
    `,
    {
      name: 'deleteWatch',
      options: (ownProps) => {
        const { listing } = ownProps;
        return {
          variables: {
            input: { listingId: listing.id },
          },
          optimisticResponse: {
            deleteWatch: {
              __typename: 'core_apimessages_UpdateWatchResponse',
              id: -1,
              listing: {
                id: listing.id,
                _id: listing._id,
                watching: false,
                __typename: 'Listing',
              },
            },
          },
        };
      },
    },
  );

async function handleWatch(props: IProps) {
  const {
    listing,
    position,
    parentComponentName,
    deleteWatch,
    createWatch,
  } = props;

  const watching = listing.watching;

  const productActionType = watching ?
    ProductActionType.RemoveFromWishlist : ProductActionType.AddToWishlist;
  const eventName = watching ?
    MParticleEventName.RemoveFromWishlist : MParticleEventName.AddToWishlist;

  try {
    await (watching ? deleteWatch() : createWatch());

    trackEvent({
      productActionType,
      eventName,
      listing,
      position,
      componentName: parentComponentName,
    });
  } catch (e) {
    elog.error(
      e.message,
      { eventName, componentName: COMPONENT_NAME, listingId: listing.id },
      e,
    );
  }
}

function handleClick(evt, props: IProps, userLoggedOut: boolean, handleLoggedOutUser: (evt) => void) {
  evt.preventDefault();
  evt.stopPropagation();

  const {
    listing,
    position,
    parentComponentName,
  } = props;

  if (userLoggedOut) {
    trackEvent({
      productActionType: ProductActionType.Click,
      eventName: MParticleEventName.LoggedOutWatch,
      listing,
      position,
      componentName: parentComponentName,
    });

    localStorage.setItem(WATCH_KEY, listing.id);
    handleLoggedOutUser(evt);
    return;
  }

  handleWatch(props);
}

export const WatchBadge = flowRight([
  createWatch,
  deleteWatch,
])(InternalWatchBadge);
